/** @jsxImportSource @emotion/react */
import * as Sentry from "@sentry/react";
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
  InMemoryCacheConfig,
  Observable,
  ServerError
} from "@apollo/client";
import {HotkeysProvider} from "@blueprintjs/core";
import {css} from "@emotion/react";
import styled from "@emotion/styled";
import {OktaAuth, toRelativeUrl} from "@okta/okta-auth-js";
import {Security} from "@okta/okta-react";
import {LicenseManager} from "@ag-grid-enterprise/core";
import {AUTH_TYPE, AuthOptions, createAuthLink} from "aws-appsync-auth-link";
import {createSubscriptionHandshakeLink} from "aws-appsync-subscription-link";
import {ConfigCatProvider, createConsoleLogger, LogLevel, PollingMode} from "configcat-react";
import {useCallback, useState} from "react";
import {ConfigProvider} from "react-avatar";
import {useHistory} from "react-router-dom";
import {CenterType, Fill, Top, ViewPort} from "react-spaces";
import {AppContextProvider} from "./ApplicationContext";
import AppRoutes from "./AppRoutes";
import AuthProvider from "./providers/AuthProvider";
import {Constants, UserPreferences} from "./constants/constants";
import DataDogProvider from "./providers/DataDogProvider";
import {LoadingSpinner} from "./components/common/LoadingSpinner";
import ErrorBoundaryFallback from "./components/common/ErrorBoundaryFallback";
import {CONFIG_CAT_KEY, CONFIG_CAT_REFRESH_INTERVAL, env, GQL_API_URL, oktaAuthConfig, REGION} from "./config";
import "./App.css";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFnsV3";
import FeatureFlagProvider from "./providers/FeatureFlagProvider";
import DispatchConfigurationProvider from "./providers/DispatchConfigurationProvider";
import PreferenceProvider from "./providers/PreferenceProvider";
import {onError} from "@apollo/client/link/error";
import {AppToaster} from "./utils/toasters/toaster";
import {throttle} from "lodash";
import OktaConfigProvider from "./providers/OktaConfigProvider";
import DispatchNavBar from "./components/navigation/DispatchNavBar";
import DispatchGroupProvider from "./providers/DispatchGroupProvider";
import {AssignmentProvider} from "./providers/AssignmentProdiver";
import DispatchStationProvider from "./providers/DispatchStationProvider";
import log from "loglevel";
import DispatchDataProvider from "./providers/DispatchDataProvider";
import {ManifestDetailsProvider} from "./providers/ManifestDetailsProvider";
import {AssignmentViewProvider} from "./providers/AssignmentViewProvider";
import {CreateManifestProvider} from "./providers/CreateManifestProvider";
import SentryProvider from "./providers/SentryProvider";

LicenseManager.setLicenseKey(env.aggridLicenseKey);

const oktaAuth = new OktaAuth(oktaAuthConfig);

oktaAuth.start();
let renewalPromise: Promise<any> | null = null;

oktaAuth.tokenManager.off("expired");
oktaAuth.tokenManager.off("added");
oktaAuth.tokenManager.off("renewed");
oktaAuth.tokenManager.off("removed");
oktaAuth.tokenManager.off("error");

oktaAuth.tokenManager.on("expired", (token) => {
  log.debug("[Okta Token Manager] Token Expired", token);
});

oktaAuth.tokenManager.on("added", (token) => {
  log.debug("[Okta Token Manager] Token Added", token);
});

oktaAuth.tokenManager.on("renewed", (token) => {
  log.debug("[Okta Token Manager] Token Renewed", token);
});

oktaAuth.tokenManager.on("removed", (token) => {
  log.debug("[Okta Token Manager] Token Removed", token);
});

oktaAuth.tokenManager.on("error", (error) => {
  if (
    error.errorCode === "invalid_grant" &&
    error.tokenKey === "accessToken" &&
    error.errorSummary === "The refresh token is invalid or expired."
  ) {
    log.info("[Okta Token Manager] Refresh Token Expired", error);
    log.info("[Okta Token Manager] Signing out user " + window.location.origin);
    //Our lifetime for the refresh token has expired, so we need to sign in again
    oktaAuth
      .signOut({
        postLogoutRedirectUri: window.location.origin
      })
      .catch((error) => {
        log.error(`Error signing user out of okta ${error.message}`, error);
      });
  } else {
    log.error(`[Okta Token Manager] Token Error - ${JSON.stringify(error)}`);
  }
});

// All polling and datasource queries use no-cache, so the Apollo cache is
// effectively unused for the main data path. Only 3 minor queries still hit the
// cache (vehicle types, freemium usage, driver selector). Keep config minimal.
const apolloCacheConfig: InMemoryCacheConfig = {};

const App = () => {
  const history = useHistory();
  const login = async () => history.push("/");
  const logout = async () => {
    window.sessionStorage.removeItem(UserPreferences.jobGridFilterState);
    oktaAuth.signOut().catch((error) => {
      log.error(`Error signing user out of okta ${error.message}`, error);
    });
  };

  const [, setRefreshState] = useState({});
  const [loading, setLoading] = useState(false);
  const configCatLogger = createConsoleLogger(LogLevel.Error);

  const restoreOriginalUri = async (_oktaAuth: any, originalUri: any) => {
    history.replace(toRelativeUrl(originalUri || "/", window.location.origin));
  };

  const region = REGION;
  const auth: AuthOptions = {
    type: AUTH_TYPE.AWS_LAMBDA,
    token: async () => {
      const accessToken = await oktaAuth.getAccessToken();
      return accessToken ? `Bearer ${accessToken}` : "";
    }
  };

  const httpLink = createHttpLink({
    uri: GQL_API_URL
  });

  const errorLink = onError(({graphQLErrors, networkError, operation, forward}) => {
    // Capture GraphQL errors to Sentry
    if (graphQLErrors) {
      graphQLErrors.forEach((gqlError) => {
        Sentry.captureException(gqlError, {
          tags: {
            source: "apollo-gql",
            operationName: operation.operationName
          },
          level: "error"
        });
        log.debug(`[GraphQL Link] - error: ${gqlError.message}`);
      });
    }

    if (networkError) {
      // Capture network errors to Sentry
      Sentry.captureException(networkError, {
        tags: {
          source: "apollo-network",
          statusCode: (networkError as any).statusCode || "unknown",
          operationName: operation.operationName
        },
        level: (networkError as ServerError).statusCode === 401 ? "info" : "error"
      });

      if (networkError.name === "ServerError" && (networkError as ServerError).statusCode === 401) {
        log.debug(`[GraphQL Link] - authenticationError: ${JSON.stringify(networkError)}`);

        if (!renewalPromise) {
          renewalPromise = oktaAuth.tokenManager
            .renew("accessToken")
            .then((token) => {
              log.debug("[GraphQL Link] - Token renewed successfully");
              return token;
            })
            .catch((error) => {
              log.error("[GraphQL Link] - Token renewal failed", error);
              logout();
              throw error;
            })
            .finally(() => {
              renewalPromise = null;
            });
        }

        const currentRenewalPromise = renewalPromise;

        return new Observable((observer) => {
          currentRenewalPromise
            .then((token) => {
              // Update the operation context with the new token to ensure
              // downstream links (like authLink) use the fresh credentials.
              operation.setContext(({headers = {}}) => ({
                headers: {
                  ...headers,
                  Authorization: `Bearer ${token.accessToken}`
                }
              }));

              const subscriber = forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer)
              });
              return () => subscriber.unsubscribe();
            })
            .catch((error) => {
              observer.error(error);
            });
        });
      } else {
        //TODO: Handle this error - Possible logout
        if (oktaAuth.authStateManager.getAuthState()?.isAuthenticated) {
          log.error("[GraphQL Link] - networkError:", networkError, {
            graphQLErrors,
            networkError
          });
        }
      }
    }
  });

  const clockCheckLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      try {
        const context = operation.getContext();
        if (context.response?.headers) {
          const dateHeader = context.response.headers.get("Date");
          const serverTime = new Date(dateHeader).getTime();
          const systemTime = new Date().getTime();
          const diff = Math.abs(serverTime - systemTime);
          if (diff >= Constants.CLOCK_SKEW_WARNING_THRESHOLD_MILLIS) {
            log.warn("Clock skew detected. System time = " + systemTime + ", server time = " + serverTime);
            throttleClockSkewWarning();
          }
        }
        return response;
      } catch (e) {
        log.error("[GraphQL Link] - Failed in clockCheckLink", e);
        return response;
      }
    });
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttleClockSkewWarning = useCallback(
    throttle(() => {
      AppToaster.show({
        icon: "warning-sign",
        intent: "danger",
        message: "Clock skew detected. Local system time is out of sync, you may encounter errors"
      });
    }, 30000),
    []
  );

  const link = ApolloLink.from([
    errorLink,
    clockCheckLink,
    createAuthLink({url: GQL_API_URL, region, auth}),
    createSubscriptionHandshakeLink({url: GQL_API_URL, region, auth}, httpLink)
  ]);

  const client = new ApolloClient({
    // connectToDevTools: true,
    connectToDevTools: process.env.NODE_ENV !== "production",
    link: link,
    cache: new InMemoryCache(apolloCacheConfig),
    defaultOptions: {
      watchQuery: {fetchPolicy: "no-cache"},
      query: {fetchPolicy: "no-cache"}
    }
  });

  const AppContainer = styled.div({
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap"
  });

  const splitter = css(`
        .spaces-resize-handle {
            background-color: #F6F7F9
        }
    `);

  const refresh = () => {
    setRefreshState({});
  };

  const resetCache = () => client.resetStore();

  const mainLoading = (isLoading: boolean) => {
    setLoading(isLoading);
  };

  const renderContent = () => {
    if (loading) {
      return <LoadingSpinner />;
    }
    return <AppRoutes />;
  };

  return (
    <AppContainer>
      <ApolloProvider client={client}>
        <ConfigProvider
          colors={[
            "#1F4B99",
            "#3A729E",
            "#6096A1",
            "#93B9A2",
            "#D7D7A1",
            "#F8CE87",
            "#E7A55D",
            "#D27D3A",
            "#B9561F",
            "#9E2B0E"
          ]}
          avatarRedirectUrl="https://avatar-redirect.appspot.com"
        >
          <HotkeysProvider>
            <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
              <AuthProvider>
                <DispatchConfigurationProvider>
                  <ConfigCatProvider
                    sdkKey={CONFIG_CAT_KEY}
                    pollingMode={PollingMode.AutoPoll}
                    options={{pollIntervalSeconds: CONFIG_CAT_REFRESH_INTERVAL, logger: configCatLogger}}
                  >
                    <FeatureFlagProvider>
                      <SentryProvider>
                        <OktaConfigProvider>
                          <DataDogProvider>
                            <AppContextProvider>
                              <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <PreferenceProvider>
                                  <DispatchDataProvider>
                                    <DispatchStationProvider>
                                      <DispatchGroupProvider>
                                        <AssignmentViewProvider>
                                          <AssignmentProvider>
                                            <ManifestDetailsProvider>
                                              <CreateManifestProvider>
                                                <Sentry.ErrorBoundary
                                                  fallback={(errorData) => (
                                                    <ErrorBoundaryFallback
                                                      error={errorData.error as Error}
                                                      componentStack={errorData.componentStack}
                                                    />
                                                  )}
                                                  onError={(error, errorInfo) => {
                                                    log.error("Sentry Error Boundary caught error:", error, errorInfo);
                                                  }}
                                                >
                                                  <ViewPort className="App" css={splitter}>
                                                    <Top size={52} centerContent={CenterType.Vertical}>
                                                      <DispatchNavBar
                                                        authLogout={logout}
                                                        authLogin={login}
                                                        refresh={refresh}
                                                        mainLoading={mainLoading}
                                                        resetCache={resetCache}
                                                      />
                                                    </Top>
                                                    <Fill>{renderContent()}</Fill>
                                                  </ViewPort>
                                                </Sentry.ErrorBoundary>
                                              </CreateManifestProvider>
                                            </ManifestDetailsProvider>
                                          </AssignmentProvider>
                                        </AssignmentViewProvider>
                                      </DispatchGroupProvider>
                                    </DispatchStationProvider>
                                  </DispatchDataProvider>
                                </PreferenceProvider>
                              </LocalizationProvider>
                            </AppContextProvider>
                          </DataDogProvider>
                        </OktaConfigProvider>
                      </SentryProvider>
                    </FeatureFlagProvider>
                  </ConfigCatProvider>
                </DispatchConfigurationProvider>
              </AuthProvider>
            </Security>
          </HotkeysProvider>
        </ConfigProvider>
      </ApolloProvider>
    </AppContainer>
  );
};

export default App;
export {apolloCacheConfig};
